import { useEffect, useState } from 'react'

/**
 * Egeszen kicsi utvonalkezelo.
 *
 * Nehany lapos oldal van (fooldal, arbecsles, rendeleskovetes, ajanlat,
 * fizetes, jogi dokumentumok, admin), es a React Router ~15 kB-ot es egy
 * fuggoseget jelentene ezert. Ez a valtozat a `history` API-t hasznalja,
 * kezeli a vissza gombot, es ennyi.
 *
 * ────────────────────────────────────────────────────────────────────
 * FONTOS ELESITESI FELTETEL
 *
 * A kiszolgalot ugy kell beallitani, hogy MINDEN ismeretlen utat az
 * `index.html`-re iranyitson (SPA fallback), es az oldal a domain
 * GYOKEREBOL szolgaljon ki (lasd `vite.config.ts`, `base: '/'`).
 *
 * Enelkul ket dolog romlik el:
 *   - a `/ajanlat/VX-...` link kozvetlen megnyitasa 404-et adna - pont az
 *     a link, amit e-mailben kikuldunk;
 *   - a `/arbecsles` oldal frissitese szinten 404 lenne.
 * ────────────────────────────────────────────────────────────────────
 */

export type Route =
  | { name: 'home' }
  | { name: 'estimate' }
  | { name: 'track' }
  | { name: 'quote'; slug: string }
  | { name: 'payment'; token: string }
  | {
      name: 'legal'
      document: 'impresszum' | 'aszf' | 'adatkezeles' | 'sutik' | 'fogyasztoi-tajekoztato'
    }
  | { name: 'admin' }

/** A `/arbecsles` oldal utvonala. Egy helyen all, hogy ne csusszon el. */
export const ESTIMATE_PATH = '/arbecsles'

/**
 * A RENDELESKOVETES UTVONALA.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NINCS AZONOSITO AZ UTBAN
 *
 * Kezenfekvo lenne `/rendeleskovetes/VX-260819-7F3A`, de az visszahozna
 * pont azt a bajt, ami ellen a szerveroldali parositas vedekezik. A
 * rendelesszamban mindossze NEGY veletlen karakter van (a tobbi a
 * rendeles napja), tehat egy ilyen cim kitalalhato - es ha kitalalhato,
 * akkor MEGOSZTHATO es HIVATKOZASBAN IS ELSZIVAROG: a `Referer` fejlec
 * minden kimeno kattintassal viszi magaval.
 *
 * Ezert az azonosito URLAPON megy be, nem az utvonalban, es a szerver
 * e-mail cimet is ker hozza (lasd `order_track_public`).
 *
 * ELESITESI FELTETEL: ugyanaz az SPA fallback, mint a tobbi utvonalnal -
 * a `/rendeleskovetes` kozvetlen megnyitasa es frissitese kulonben 404.
 * ────────────────────────────────────────────────────────────────────
 */
export const TRACK_PATH = '/rendeleskovetes'

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean)

  const legalDocuments = [
    'impresszum',
    'aszf',
    'adatkezeles',
    'sutik',
    'fogyasztoi-tajekoztato'
  ] as const

  if (legalDocuments.includes(parts[0] as (typeof legalDocuments)[number])) {
    return { name: 'legal', document: parts[0] as (typeof legalDocuments)[number] }
  }

  if (parts[0] === 'ajanlat' && parts[1]) {
    return { name: 'quote', slug: decodeURIComponent(parts[1]) }
  }
  /*
    A FIZETESI OLDAL: `/fizetes/<token>`.

    Ez a cim STABIL - ez kerul a dijbekerore es a fizetesi levelbe. A
    szolgaltatoi (myPOS) URL SZANDEKOSAN nem: az lejar es ujrageneralaskor
    megvaltozik, tehat egy ket hete kikuldott bizonylaton mar halott lenne.

    A tokent itt NEM ellenorizzuk alakilag: azt az oldal teszi, es igy egy
    elgepelt link is ertheto uzenetet kap, nem a fooldalra dobja a latogatot.
  */
  if (parts[0] === 'fizetes' && parts[1]) {
    return { name: 'payment', token: decodeURIComponent(parts[1]) }
  }
  /*
    Az ekezetes valtozatot is elfogadjuk: a bongeszo a cimsorbol
    `/%C3%A1rbecsl%C3%A9s` alakban kuldi, es aki kezzel irja be, jo esellyel
    ekezettel irja.
  */
  if (parts[0] && ['arbecsles', 'árbecslés'].includes(decodeURIComponent(parts[0]).toLowerCase())) {
    return { name: 'estimate' }
  }
  /*
    A RENDELESKOVETES. Ugyanaz az ekezet-turés, mint az arbecslesnel: aki
    kezzel irja be, jo esellyel ekezettel irja, a bongeszo pedig
    `/rendel%C3%A9sk%C3%B6vet%C3%A9s` alakban kuldi vissza.
  */
  if (
    parts[0] &&
    ['rendeleskovetes', 'rendeléskövetés'].includes(decodeURIComponent(parts[0]).toLowerCase())
  ) {
    return { name: 'track' }
  }
  if (parts[0] === 'admin') return { name: 'admin' }
  return { name: 'home' }
}

/**
 * Utvonalvaltas.
 *
 * A `pushState` maga NEM valt ki `popstate` esemenyt (azt csak a
 * vissza/elore gomb teszi), ezert kezzel kuldunk egyet - igy a `useRoute`
 * ugyanugy ertesul a sajat navigaciorol, mint a bongeszo gombjairol.
 */
export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (options.replace) history.replaceState({}, '', path)
  else history.pushState({}, '', path)
  dispatchEvent(new PopStateEvent('popstate'))
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname))

  useEffect(() => {
    const onChange = (): void => setRoute(parseRoute(location.pathname))
    addEventListener('popstate', onChange)
    return () => removeEventListener('popstate', onChange)
  }, [])

  return route
}

/**
 * A FOOLDALI SZAKASZOK ELERESE BARMELYIK OLDALROL.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NEM ELEG A `#folyamat`
 *
 * A `/arbecsles` vagy `/ajanlat/VX-...` oldalon a puszta `#folyamat`
 * hivatkozas az AKTUALIS ut egy nem letezo horgonyara mutat: a bongeszo
 * elhelyezi a `#folyamat`-ot a cimsorban, es NEM tortenik semmi. A latogato
 * ugy latja, hogy a lablec linkjei nem mukodnek.
 *
 * Ezert a hivatkozas mindig `/#folyamat` alaku (tehat a gyokerhez kotott),
 * es ha epp nem a fooldalon vagyunk, eloszb odanavigalunk, majd
 * gorgetunk.
 * ────────────────────────────────────────────────────────────────────
 */
export const sectionHref = (hash: string): string => `/#${hash}`

export function goToSection(hash: string): void {
  const target = (): void => {
    const el = document.getElementById(hash)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (location.pathname === '/') {
    history.replaceState({}, '', `/#${hash}`)
    target()
    return
  }

  navigate(`/#${hash}`)
  /*
    A fooldal ekkor mount-ol. Egy `requestAnimationFrame` utan mar a DOM-ban
    van a szakasz; ha megsem, a masodik probalkozas elkapja. Nem `setTimeout`
    fix kesleltetessel: az vagy tul rovid, vagy feleslegesen lassu.
  */
  requestAnimationFrame(() => {
    target()
    requestAnimationFrame(target)
  })
}

/** A megoszthato ajanlati URL: `https://voxora.hu/ajanlat/VX-260811-7F3A-K9Q2M7DX`. */
export function quoteUrl(slug: string): string {
  return `${location.origin}/ajanlat/${slug}`
}
